import type { User } from '../modules/auth/types'

/**
 * Interface định nghĩa state của auth store
 * @interface State
 */
export interface State {

  /** Thông tin người dùng đã đăng nhập hoặc null nếu chưa đăng nhập */
  user: User | null

  /** Trạng thái đang chờ xác thực 2FA */
  waiting2FA: boolean
}

/**
 * Interface định nghĩa getters của auth store
 * @interface Getters
 */
export interface Getters {

  /** Trạng thái đã đăng nhập hay chưa */
  loggedIn: boolean
}

/**
 * Interface định nghĩa actions của auth store
 * @interface Actions
 */
export interface Actions {

  /**
   * Thiết lập thông tin người dùng
   * @param {User | null} user - Thông tin người dùng hoặc null để đăng xuất
   */
  setUser(user: User | null): void
}

/**
 * Store quản lý trạng thái xác thực người dùng
 * @description Quản lý thông tin đăng nhập, đăng xuất và phân quyền người dùng
 */
export const useAuthStore = defineStore('auth', {
  /**
   * Khởi tạo state của store
   * @returns {State} State ban đầu của auth store
   */
  state: (): State => {
    return {
      user: null,
      waiting2FA: false,
    }
  },

  getters: {
    /**
     * Kiểm tra người dùng đã đăng nhập hay chưa
     * @returns {boolean} true nếu đã đăng nhập, false nếu chưa
     */
    loggedIn(): boolean {
      return !!this.user
    },

    /**
     * Kiểm tra người dùng có phải là admin hay không
     * @returns {boolean} true nếu là admin, false nếu không phải
     */
    isAdmin(): boolean {
      return this.user?.type === 'admin'
    },
  },

  actions: {
    /**
     * Thiết lập thông tin người dùng
     * @description Cập nhật thông tin người dùng trong store
     * @param {User | null} user - Thông tin người dùng hoặc null để đăng xuất
     */
    setUser(user: User | null) {
      this.user = user
    },

    /**
     * Thiết lập trạng thái đang chờ xác thực 2FA
     * @description Cập nhật trạng thái đang chờ xác thực 2FA
     * @param {boolean} waiting2FA - Trạng thái đang chờ xác thực 2FA
     */
    setWaiting2FA(waiting2FA: boolean) {
      this.waiting2FA = waiting2FA
    },
  },
})

/**
 * Cho phép cập nhật Hot Module Replacement cho store
 */
acceptHMRUpdate(useAuthStore, import.meta.hot)
